import { TeamBand } from "@/components/TeamBand";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ShieldAlert, Leaf, FileCheck, ArrowRight, Check, Target, Users, Award, HeartHandshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LeadMagnet } from "@/components/LeadMagnet";

const IMG = "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/";
const HERO = IMG + "9563f04ad372b834f164653eb250e8f1f5f84d3a2263a405bbd08fe952e9f4a3.jpeg";
const QSE = IMG + "ceb19fe37a06e7f6d16b026e420a55f44d61aa06b5dea6ca6bf659807c8f01a0.jpeg";
const QVT = IMG + "808a8358aa90e983a6ab129c815f76fb0ae042016d6c82da295e254dfceaea69.jpeg";
const RSE = IMG + "e63d21cbca4722758b4f43066dae20cdb3d9861b64920a18a28064d56b288de1.jpeg";
const METHOD = IMG + "e3a1307624635c6db1b8701d111f6c99be61dc4bca2831e8cb3aab463f1ff09a.jpeg";

const STATS = [
  ["15+", "ans d'expertise"],
  ["500+", "entreprises accompagnées"],
  ["98%", "de clients satisfaits"],
  ["3", "référentiels ISO maîtrisés"],
];

const SERVICES = [
  { icon: FileCheck, t: "Document Unique (DUERP)", d: "Rédaction et mise à jour de votre document unique d'évaluation des risques professionnels, conforme à la réglementation." },
  { icon: ShieldAlert, t: "Analyse des risques", d: "Identification, évaluation et hiérarchisation des risques sur vos sites, avec plan d'action priorisé." },
  { icon: ClipboardCheck, t: "Audit & conformité", d: "Audits QSE, préparation aux certifications ISO 9001, 45001 et 14001, suivi des non-conformités." },
  { icon: Leaf, t: "Management environnemental", d: "Accompagnement à la réduction de l'empreinte, gestion des déchets et conformité environnementale." },
];

const STEPS = [
  ["01", "Diagnostic", "Nous analysons votre organisation, vos risques et votre niveau de conformité."],
  ["02", "Feuille de route", "Un plan d'action clair, priorisé et adapté à vos moyens et à vos échéances."],
  ["03", "Mise en œuvre", "Nous déployons les actions avec vos équipes : outils, procédures, formations."],
  ["04", "Suivi & amélioration", "Indicateurs, audits de suivi et amélioration continue dans la durée."],
];

const PILLARS = [
  {
    tag: "QSE", title: "Qualité, Sécurité, Environnement", img: QSE,
    desc: "Structurez un système de management performant et conforme, de l'audit initial à la certification, pour protéger vos collaborateurs et fiabiliser vos processus.",
    points: ["Document unique & analyse des risques", "Certifications ISO 9001 / 45001 / 14001", "Audits internes & plans d'action", "Culture sécurité et prévention"],
  },
  {
    tag: "QVT", title: "Qualité de Vie et Conditions de Travail", img: QVT,
    desc: "Améliorez le bien-être, l'engagement et la performance de vos équipes grâce à une démarche QVCT concrète et à la prévention des risques psychosociaux.",
    points: ["Diagnostic QVCT & enquête bien-être", "Prévention des RPS et du stress", "Ergonomie des postes & organisation", "Management bienveillant & cohésion"],
  },
  {
    tag: "RSE", title: "Responsabilité Sociétale des Entreprises", img: RSE,
    desc: "Faites de la RSE un levier de confiance et de compétitivité : stratégie durable, empreinte environnementale et reporting aligné sur les référentiels ISO 26000 et CSRD.",
    points: ["Stratégie & feuille de route RSE", "Bilan carbone & réduction d'impact", "Reporting extra-financier (CSRD)", "Diversité, inclusion & achats responsables"],
  },
];

const VALUES = [
  { icon: Target, t: "Sur mesure", d: "Des solutions adaptées à votre secteur, votre taille et votre maturité." },
  { icon: Users, t: "Avec vos équipes", d: "Nous impliquons vos collaborateurs pour des démarches vraiment ancrées." },
  { icon: Award, t: "Experts certifiés", d: "Consultants et formateurs agréés, à jour des dernières exigences." },
  { icon: HeartHandshake, t: "Dans la durée", d: "Un accompagnement pérenne, au-delà de la simple mise en conformité." },
];

export default function Services() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="services-page" className="pt-[148px]">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Cabinet de conseil · QSE · QVT · RSE</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">Le conseil qui engage des résultats.</h1>
            <p className="mt-6 text-lg md:text-xl max-w-xl" style={{ color: "var(--ink-2)" }}>
              De la mise en conformité à l'excellence opérationnelle, nous conseillons dirigeants, DRH et responsables HSE avec l'exigence d'un grand cabinet : diagnostics rigoureux, recommandations chiffrées, impact mesuré.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-devis-btn" className="btn-primary px-8 py-4 font-semibold flex items-center justify-center gap-2">Parler à un expert <ArrowRight size={18} /></Link>
              <Link to="/formations" className="btn-outline px-8 py-4 font-semibold flex items-center justify-center gap-2">Voir les formations</Link>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.12}>
            <div className="overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img src={HERO} alt="Équipe de consultants QSE diversifiée" className="w-full h-80 lg:h-[460px] object-cover" data-testid="services-hero-image" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section style={{ background: "var(--ink)", color: "var(--bone)" }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div>
                <p className="font-display font-black text-4xl lg:text-5xl" style={{ color: "var(--orange)" }}>{n}</p>
                <p className="mt-2 text-sm" style={{ color: "rgba(245,245,240,0.75)" }}>{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Nos practices</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none max-w-3xl mb-16">Trois practices, une vision intégrée de la performance durable.</h2></Reveal>
        {PILLARS.map((b, i) => (
          <Reveal key={b.tag} delay={0.05}>
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-20 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`pillar-${b.tag.toLowerCase()}`}>
              <div className="overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <img src={b.img} alt={b.title} className="w-full h-72 lg:h-96 object-cover" />
              </div>
              <div>
                <span className="font-display font-black text-6xl" style={{ color: "var(--orange)" }}>{b.tag}</span>
                <h3 className="font-display font-bold text-3xl mt-3 mb-4">{b.title}</h3>
                <p className="text-lg mb-6" style={{ color: "var(--ink-2)" }}>{b.desc}</p>
                <ul className="space-y-3">
                  {b.points.map((p) => (
                    <li key={p} className="flex items-center gap-3"><Check size={18} style={{ color: "var(--orange)" }} /> {p}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* MÉTHODOLOGIE */}
      <section style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <img src={METHOD} alt="Atelier de travail collaboratif" className="w-full h-80 lg:h-[440px] object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Notre méthode</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-10">Une méthodologie éprouvée, du diagnostic à l'impact.</h2></Reveal>
            <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
              {STEPS.map(([n, t, d], i) => (
                <Reveal key={n} delay={i * 0.06}>
                  <div className="flex gap-5 p-6" style={{ background: "var(--bone)" }}>
                    <span className="font-mono2 text-sm shrink-0" style={{ color: "var(--orange)" }}>{n}</span>
                    <div>
                      <h3 className="font-display font-bold text-xl mb-1">{t}</h3>
                      <p style={{ color: "var(--ink-2)" }}>{d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DOMAINES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Domaines d'intervention</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-12">Prestations QSE clés</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.t} delay={(i % 2) * 0.08}>
                <div className="p-10 md:p-14 h-full" style={{ background: "var(--white)" }} data-testid={`service-${i}`}>
                  <Icon size={36} strokeWidth={1.6} style={{ color: "var(--orange)" }} />
                  <h3 className="font-display font-bold text-2xl mt-6 mb-3">{s.t}</h3>
                  <p className="text-lg" style={{ color: "var(--ink-2)" }}>{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* VALEURS */}
      <section style={{ background: "var(--ink)", color: "var(--bone)" }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Pourquoi ALDOL-SOLUTIONS</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-14">Ce qui fait la différence</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.t} delay={i * 0.08}>
                  <Icon size={32} strokeWidth={1.6} style={{ color: "var(--orange)" }} />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2">{v.t}</h3>
                  <p style={{ color: "rgba(245,245,240,0.7)" }}>{v.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INCLUSION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-4">
        <Reveal>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-10 md:p-14" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="inclusion-banner">
            <div>
              <p className="overline mb-3" style={{ color: "var(--orange)" }}>Inclusion & handicap</p>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none max-w-xl">Faciliter l'insertion des personnes en situation de handicap</h2>
              <p className="mt-4 text-lg max-w-xl" style={{ color: "var(--ink-2)" }}>Aménagement de poste, exosquelettes d'assistance, financements AGEFIPH : découvrez notre accompagnement dédié.</p>
            </div>
            <Link to="/inclusion-handicap" data-testid="inclusion-banner-link" className="btn-primary px-8 py-4 font-semibold whitespace-nowrap flex items-center gap-2">Découvrir <ArrowRight size={18} /></Link>
          </div>
        </Reveal>
      </section>

      {/* LIVRE BLANC */}
      <LeadMagnet />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-10 md:p-16" style={{ background: "var(--orange)", color: "var(--bone)" }}>
            <div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">Prêt à passer à l'action ?</h2>
              <p className="mt-4 text-lg md:text-xl max-w-lg" style={{ color: "rgba(245,245,240,0.9)" }}>Échangeons sur vos enjeux QSE, QVT et RSE. Premier rendez-vous offert.</p>
            </div>
            <Link to="/contact" data-testid="services-contact-btn" className="px-10 py-5 font-display font-bold text-lg whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)" }}>Nous contacter</Link>
          </div>
        </Reveal>
      </section>
      <TeamBand />
    </div>
  );
}
